import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { QtyInput } from "@/components/StockQty";

import { fmtDate } from "@/lib/format";

export default function StoreReturns() {
  const { can } = usePermissions();
  const canCreateMovement = can("STOCK_MOVEMENTS", "create");
  const canPostMovement = can("STOCK_MOVEMENTS", "post");
  const [returns, setReturns] = useState([]);
  const [issues, setIssues] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [srnRes, ginRes, itemRes] = await Promise.all([
        api.get("/inventory/store-returns"), api.get("/inventory/goods-issues"), api.get("/inventory/items"),
      ]);
      setReturns(srnRes.data);
      setIssues(ginRes.data.filter((g) => g.status === "POSTED"));
      setItems(itemRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const itemName = (id) => { const i = items.find((it) => it.id === id); return i ? `${i.code} — ${i.name}` : id; };
  const unitOf = (id) => items.find((it) => it.id === id)?.uom_code || "";

  const post = async (srn) => {
    setBusyId(srn.id);
    try {
      await api.post(`/inventory/store-returns/${srn.id}/post`);
      toast.success("Store return posted — stock is back on hand");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="store-returns-page">
      <PageHeader title="Store Returns" description="Unused material returned from site, back into stores — at the original issue cost." action={canCreateMovement && <CreateSRNDialog issues={issues} itemName={itemName} unitOf={unitOf} onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && returns.length === 0 ? (
          <EmptyState title="No store returns yet" testId="store-returns-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {returns.map((srn) => (
                <TableRow key={srn.id} data-testid={`srn-row-${srn.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{srn.number}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(srn.return_date)}</TableCell>
                  <TableCell><StatusPill value={srn.status} /></TableCell>
                  <TableCell className="text-right">
                    {canPostMovement && srn.status === "DRAFT" && (
                      <Button size="sm" variant="outline" disabled={busyId === srn.id} onClick={() => post(srn)} data-testid={`post-srn-button-${srn.number}`}>
                        <Undo2 className="h-3.5 w-3.5" /> Post
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateSRNDialog({ issues, itemName, unitOf, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ goods_issue_id: "", return_date: "" });
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);

  const selectedIssue = issues.find((g) => g.id === form.goods_issue_id);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const items = Object.entries(qty).filter(([, q]) => q).map(([goods_issue_item_id, quantity]) => ({ goods_issue_item_id, quantity }));
      await api.post("/inventory/store-returns", { ...form, items });
      toast.success("Store return created");
      setForm({ goods_issue_id: "", return_date: "" });
      setQty({});
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-srn-button"><Plus className="h-4 w-4" /> New Store Return</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Store Return</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-srn-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Goods issue</Label>
              <Select required value={form.goods_issue_id} onValueChange={(v) => setForm({ ...form, goods_issue_id: v })}>
                <SelectTrigger data-testid="srn-gin-select" className="mt-1.5"><SelectValue placeholder="Select a posted GIN" /></SelectTrigger>
                <SelectContent>{issues.map((g) => <SelectItem key={g.id} value={g.id}>{g.number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Return date</Label><Input required type="date" data-testid="srn-date-input" value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} className="mt-1.5" /></div>
          </div>
          {selectedIssue && (
            <div className="space-y-2">
              <Label>Return qty (issued − already returned shown for reference)</Label>
              {selectedIssue.items.map((it) => (
                <div key={it.id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 text-muted-foreground">{itemName(it.item_id)} (issued {it.quantity}, returned {it.returned_qty})</span>
                  <QtyInput className="w-28" unit={unitOf(it.item_id)} value={qty[it.id] || ""} onChange={(v) => setQty({ ...qty, [it.id]: v })} data-testid={`srn-item-qty-input-${it.id}`} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving || !selectedIssue} data-testid="create-srn-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
